import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { CookieStorage } from 'cookie-storage';

import { ACCESS_TOKEN_COOKIE_KEY, REFRESH_ATTEMPT_SESSION_KEY, REFRESH_TOKEN_COOKIE_KEY } from '@pflegia/types/common/constants/tokens';
import { REFRESH_TOKEN } from '@pflegia/ui/application/identity/operations/refresh-token';
import { removeUtmKeysFromCookies } from '@pflegia/ui/application/marketing/utm/utils/remove-utm-keys-from-cookies';
import { HOME } from '@pflegia/ui/pflegia/constants/routes';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { getTopLevelDomain } from '@pflegia/util/common/get-top-level-domain';
import { getSourceDomainFromClient } from '@pflegia/util/pflegia/source-domain/get-source-domain-from-client';
import { transformBackendEndpoint } from '@pflegia/util/pflegia/source-domain/transform-backend-endpoint';

const MAX_REFRESH = 2;

export class AuthService {
  cookieStorage: CookieStorage;
  private pendingRefresh: Promise<void> | null = null;
  private client = new ApolloClient({
    cache: new InMemoryCache(),
    link: new HttpLink({
      uri: transformBackendEndpoint(process.env.NEXT_PUBLIC_BACKEND_ENDPOINT, getSourceDomainFromClient()),
    }),
  });

  constructor(cookieStorageParam: CookieStorage) {
    this.cookieStorage = cookieStorageParam;
  }

  get isLoggedIn(): boolean {
    return Boolean(this.refreshToken);
  }

  get accessToken() {
    return this.cookieStorage.getItem(ACCESS_TOKEN_COOKIE_KEY);
  }

  private get refreshToken() {
    return this.cookieStorage.getItem(REFRESH_TOKEN_COOKIE_KEY);
  }

  private get refreshAttempt() {
    const attempt = sessionStorage.getItem(REFRESH_ATTEMPT_SESSION_KEY);
    return attempt ? Number(attempt) : 0;
  }

  refreshTokenFromApi() {
    if (!this.pendingRefresh && this.refreshAttempt <= MAX_REFRESH) {
      this.pendingRefresh = this.client
        .mutate<{ refreshToken: boolean }>({
          context: { credentials: 'include' },
          mutation: REFRESH_TOKEN,
        })
        .then(result => {
          if (!this.pendingRefresh) return;
          this.pendingRefresh = null;
          if (!result.data?.refreshToken) {
            throw new Error();
          }
          sessionStorage.setItem(REFRESH_ATTEMPT_SESSION_KEY, '0');
        })
        .catch(() => {
          sessionStorage.setItem(REFRESH_ATTEMPT_SESSION_KEY, (this.refreshAttempt + 1).toString());
          this.logout();
        });
    }

    return this.pendingRefresh ?? Promise.resolve();
  }

  logout() {
    const domain = getTopLevelDomain(globalThis.window?.location.hostname);
    this.cookieStorage.removeItem(ACCESS_TOKEN_COOKIE_KEY, { domain });
    this.cookieStorage.removeItem(REFRESH_TOKEN_COOKIE_KEY, { domain });
    removeUtmKeysFromCookies();

    if (typeof window !== 'undefined' && window.location.pathname !== HOME) {
      window.location.href = HOME;
    }
  }
}

export const appAuthService = new AuthService(cookieStorage);
